import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import AppleIcon from '@mui/icons-material/Apple';

const stats = [
  { label: 'Exchange', value: 'Binance-ready' },
  { label: 'Execution', value: '5m to 1d intervals' },
  { label: 'Signal stack', value: 'MACD, RSI, EMA, MFI' },
];

const chips = ['Trending flow', 'Backtesting', 'Push alerts'];

function GooglePlayIcon() {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      sx={{ width: 22, height: 22, display: 'block' }}
    >
      <path d="M3 2.5L14.2 13.7L3 21.5V2.5Z" fill="#00D1FF" />
      <path d="M3 2.5L17.8 10.9L14.2 13.7L3 2.5Z" fill="#6CFF8F" />
      <path d="M3 21.5L14.2 13.7L17.9 16.2L3 21.5Z" fill="#FFD84D" />
      <path d="M17.8 10.9L21 12.7C21.6 13.1 21.6 13.9 21 14.3L17.9 16.2L14.2 13.7L17.8 10.9Z" fill="#FF6A5C" />
    </Box>
  );
}

interface StoreBadgeProps {
  href: string;
  icon: React.ReactNode;
  eyebrow: string;
  label: string;
}

function StoreBadge({ href, icon, eyebrow, label }: StoreBadgeProps) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 190,
        px: 2,
        py: 1.3,
        borderRadius: '18px',
        textDecoration: 'none',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255,255,255,0.04)',
        transition: 'transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: 'primary.main',
          bgcolor: 'rgba(21, 155, 238, 0.08)',
        },
      }}
    >
      <Box sx={{ color: 'common.white', display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.1 }}
        >
          {eyebrow}
        </Typography>
        <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Hero() {
  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: '100%',
        pt: { xs: 12, sm: 16 },
        pb: { xs: 6, sm: 10 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            theme.palette.mode === 'dark'
              ? 'radial-gradient(circle at 25% 15%, rgba(39, 191, 255, 0.24), transparent 18%), radial-gradient(circle at 78% 10%, rgba(74, 255, 196, 0.12), transparent 22%)'
              : 'radial-gradient(circle at 25% 15%, rgba(39, 191, 255, 0.16), transparent 18%)',
          pointerEvents: 'none',
        },
      })}
    >
      <Container sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={5}>
          <Stack spacing={3} sx={{ maxWidth: 760 }}>
            <Chip
              icon={<ShowChartRoundedIcon />}
              label="Web Dashboard"
              sx={{
                alignSelf: 'flex-start',
                borderRadius: '999px',
                px: 1,
                bgcolor: 'rgba(21, 155, 238, 0.14)',
                color: 'primary.light',
                border: '1px solid rgba(116, 214, 255, 0.2)',
              }}
            />
            <Typography component="h1" variant="h1" sx={{ maxWidth: 720 }}>
              Run your crypto strategy from a trading desk that feels live.
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ maxWidth: 640 }}
            >
              AlgoTrader combines indicator-driven automation, market flow, and
              backtesting into one command center. Configure strategies, monitor
              positions, and react to momentum without bouncing between tools.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap>
              <Button
                variant="contained"
                color="primary"
                component="a"
                href="https://app.algo-trader.tech/signup"
                target="_blank"
                endIcon={<ArrowOutwardRoundedIcon />}
                sx={{ borderRadius: '999px', px: 2.5, py: 1.25, boxShadow: 'none' }}
              >
                Open the dashboard
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                href="#features"
                endIcon={<NorthEastRoundedIcon />}
                sx={{
                  borderRadius: '999px',
                  px: 2.5,
                  py: 1.25,
                  borderColor: 'divider',
                  color: 'text.primary',
                }}
              >
                Explore the workflow
              </Button>
            </Stack>
           
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap>
              <StoreBadge
                href="https://apps.apple.com/sk/app/algotrader/id6458880865"
                icon={<AppleIcon sx={{ fontSize: 28 }} />}
                eyebrow="Download on the"
                label="App Store"
              />
              <StoreBadge
                href="https://play.google.com/store/apps/details?id=com.tradingbotapp"
                icon={<GooglePlayIcon />}
                eyebrow="Get it on"
                label="Google Play"
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            sx={{ alignItems: { xs: 'stretch', lg: 'flex-start' } }}
          >
            <Box
              sx={(theme) => ({
                flex: 1,
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: '32px',
                background:
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, rgba(17, 29, 48, 0.96), rgba(10, 17, 29, 0.96))'
                    : 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(240,246,252,0.95))',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 30px 80px rgba(0, 0, 0, 0.38)'
                    : '0 24px 60px rgba(17, 41, 72, 0.12)',
              })}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  pb: 2,
                }}
              >
              
                <Typography variant="caption" sx={{ letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  Trading command center
                </Typography>
              </Box>
              <Box
                component="img"
                src={require('../assets/webapp.png')}
                alt="AlgoTrader web dashboard"
                sx={{
                  width: '100%',
                  display: 'block',
                  borderRadius: '24px',
                  border: '1px solid rgba(126, 165, 214, 0.12)',
                }}
              />
            </Box>

            <Stack spacing={2} sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0 }}>
              {[
                {
                  icon: <AutoGraphRoundedIcon />,
                  title: 'Strategy presets',
                  body: 'Switch indicator combinations quickly and keep market context on screen.',
                },
                {
                  icon: <ShieldRoundedIcon />,
                  title: 'Funds stay on exchange',
                  body: 'AlgoTrader connects through your exchange account instead of custodying your assets.',
                },
              ].map((item) => (
                <Box
                  key={item.title}
                  sx={{
                    p: 2.5,
                    borderRadius: '24px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(14px)',
                  }}
                >
                  <Box sx={{ color: 'primary.light', mb: 1.5 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ mb: 0.75 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.body}
                  </Typography>
                </Box>
              ))}

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '24px',
                  border: '1px solid rgba(79, 246, 205, 0.2)',
                  bgcolor: 'rgba(79, 246, 205, 0.07)',
                }}
              >
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  Signal coverage
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {stats.map((stat) => (
                    <Box key={stat.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {stat.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
